import React, { useState, useEffect } from 'react';
import { useSummer2026 } from '../context/Summer2026Context';
import { Button } from '../components/ui/button';
import { Eye, EyeOff, Users, ArrowLeft } from 'lucide-react';
import { motion } from 'framer-motion';

export default function Summer2026LoginPage({ onSwitchMode }) {
  const { login, syncRoster, bunks } = useSummer2026();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [syncing, setSyncing] = useState(false);

  // Auto-sync bunks if not loaded
  useEffect(() => {
    if (bunks.length === 0) {
      setSyncing(true);
      syncRoster().finally(() => setSyncing(false));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!username.trim() || !password.trim()) {
      setError('Please enter both username and password');
      return;
    }
    const result = login(username, password);
    if (!result.success) {
      setError('Invalid credentials. Please try again.');
      setPassword('');
    }
  };

  const logoUrl = 'https://customer-assets.emergentagent.com/job_camp-attendance-app/artifacts/lojn686w_Riverview_Athletics_Logo.png';

  return (
    <div className="min-h-screen flex flex-col bg-[#F8FAFC]">
      <div className="bg-[#0055A4] py-8 px-4 relative">
        {onSwitchMode && (
          <button
            onClick={onSwitchMode}
            className="absolute top-4 left-4 text-white/80 hover:text-white flex items-center gap-1 text-sm"
            data-testid="back-to-programs"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
        )}
        <motion.div
          className="flex flex-col items-center"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <img
            src={logoUrl}
            alt="Rolling River Day Camp"
            className="w-32 h-32 rounded-full bg-white p-1 shadow-lg"
          />
          <h1 className="mt-4 text-2xl font-bold text-white font-['Fredoka']">
            Rolling River Day Camp
          </h1>
          <p className="text-white/80 text-sm mt-1">Summer 2026 Attendance</p>
        </motion.div>
      </div>

      <motion.div
        className="flex-1 flex items-center justify-center px-6 py-12"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <div className="w-full max-w-sm">
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-[#E2E8F0]">
            <div className="flex items-center justify-center mb-6">
              <div className="w-12 h-12 bg-[#0055A4]/10 rounded-full flex items-center justify-center">
                <Users className="w-6 h-6 text-[#0055A4]" />
              </div>
            </div>

            <h2 className="text-xl font-bold text-center text-[#0F172A] font-['Fredoka'] mb-2">
              Group Login
            </h2>
            <p className="text-sm text-center text-[#475569] mb-6">
              Enter your bunk code and bunk name to access attendance
            </p>

            {syncing && (
              <div className="text-center text-sm text-[#475569] mb-4">
                Loading bunk data...
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-[#475569] mb-1">
                  Username (Bunk Code)
                </label>
                <input
                  type="text"
                  data-testid="summer-login-username"
                  placeholder="e.g., B12"
                  value={username}
                  onChange={(e) => { setUsername(e.target.value); setError(''); }}
                  className="w-full h-14 rounded-xl border border-[#E2E8F0] bg-white text-black placeholder:text-gray-400 px-4 text-lg font-medium focus:border-[#0055A4] focus:ring-2 focus:ring-[#0055A4]/20 focus:outline-none transition-all"
                  autoComplete="off"
                  autoCapitalize="off"
                />
              </div>

              <div className="relative mb-4">
                <label className="block text-sm font-medium text-[#475569] mb-1">
                  Password (Bunk Name)
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    data-testid="summer-login-password"
                    placeholder="Enter bunk name"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(''); }}
                    className="w-full h-14 rounded-xl border border-[#E2E8F0] bg-white text-black placeholder:text-gray-400 pr-12 pl-4 text-lg font-medium focus:border-[#0055A4] focus:ring-2 focus:ring-[#0055A4]/20 focus:outline-none transition-all"
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-[#94A3B8] hover:text-[#475569] transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {error && (
                <motion.p
                  className="text-red-500 text-sm text-center mb-4"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid="summer-login-error"
                >
                  {error}
                </motion.p>
              )}

              <Button
                type="submit"
                data-testid="summer-login-submit"
                className="w-full h-14 bg-[#0055A4] hover:bg-[#004080] text-white font-bold text-lg rounded-xl shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5 active:translate-y-0"
              >
                Sign In
              </Button>
            </form>
          </div>

          <p className="text-center text-xs text-[#94A3B8] mt-6">
            Summer Camp 2026
          </p>
        </div>
      </motion.div>
    </div>
  );
}
